import React, { useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { AlertCircle, CheckCircle, XCircle } from 'lucide-react';

const BulkActionBar = ({ 
  selectedIds = [], 
  onClearSelection, 
  actions = [], 
  onExecute,
  entityType = 'items'
}) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [reason, setReason] = useState('');
  const [isExecuting, setIsExecuting] = useState(false);
  const [result, setResult] = useState(null);

  const handleActionClick = (action) => {
    setSelectedAction(action);
    setShowConfirmDialog(true);
    setReason('');
    setResult(null);
  };

  const handleConfirm = async () => {
    if (!selectedAction || !onExecute) return;

    setIsExecuting(true);
    try {
      const result = await onExecute(selectedAction.value, reason);
      setResult(result);
      
      if (result.failed_count === 0) {
        setTimeout(() => {
          setShowConfirmDialog(false);
          setSelectedAction(null);
          setReason('');
          setResult(null);
          onClearSelection();
        }, 2000);
      }
    } catch (error) {
      setResult({
        success: [],
        failed: selectedIds.map(id => ({ id, error: error.message })),
        total: selectedIds.length,
        success_count: 0,
        failed_count: selectedIds.length
      });
    } finally {
      setIsExecuting(false);
    }
  };

  const handleCancel = () => {
    setShowConfirmDialog(false);
    setSelectedAction(null);
    setReason('');
    setResult(null);
  };

  if (selectedIds.length === 0) return null;

  return (
    <>
      <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
        <div 
          className="bg-white shadow-lg rounded-lg border border-gray-200 px-6 py-4 flex items-center gap-4"
          style={{ minWidth: '600px' }}
        >
          <div className="flex-1">
            <p className="text-sm font-medium" style={{ color: '#1B3240' }}>
              {selectedIds.length} {entityType} selected
            </p>
          </div>

          <div className="flex gap-2">
            {actions.map((action) => (
              <Button
                key={action.value}
                size="sm"
                variant="outline"
                onClick={() => handleActionClick(action)}
                className={action.color || ''}
              >
                {action.icon && <action.icon className="w-4 h-4 mr-2" />}
                {action.label}
              </Button>
            ))}

            <Button
              size="sm"
              variant="ghost"
              onClick={onClearSelection}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {result ? 'Action Results' : `Confirm ${selectedAction?.label}`}
            </DialogTitle>
          </DialogHeader>

          {!result ? (
            <div className="space-y-4">
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-yellow-800">
                    You are about to {selectedAction?.label.toLowerCase()} {selectedIds.length} {entityType}
                  </p>
                  <p className="text-xs text-yellow-700 mt-1">
                    This action cannot be undone. Please confirm to proceed.
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason (optional)
                </label>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Enter reason for this action..."
                  rows={3}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {result.success_count > 0 && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-green-800">
                      Successfully processed {result.success_count} {entityType}
                    </p>
                  </div>
                </div>
              )}

              {result.failed_count > 0 && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-3 mb-3">
                    <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-red-800">
                        Failed to process {result.failed_count} {entityType}
                      </p>
                    </div>
                  </div>
                  <div className="max-h-40 overflow-y-auto">
                    {result.failed.map((failure, index) => (
                      <div key={index} className="text-xs text-red-700 py-1 border-t border-red-200">
                        ID: {failure.id.substring(0, 8)}... - {failure.error}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {!result ? (
              <>
                <Button variant="outline" onClick={handleCancel} disabled={isExecuting}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleConfirm} 
                  disabled={isExecuting}
                  style={{ backgroundColor: '#62D84E' }}
                >
                  {isExecuting ? 'Processing...' : 'Confirm'}
                </Button>
              </>
            ) : (
              <Button onClick={handleCancel}>
                Close
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BulkActionBar;
